import { StyleSheet, css } from 'aphrodite';
import PropTypes from 'prop-types';
import React from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import {
  deleteMessage,
  endConversation,
  getMessages,
  getUnreadMessageCount,
  markConversationAsRead,
  reportMessage,
  sendMessage,
} from '../../actions/messaging';
import Button from '../../components/Button';
import TextField from '../../components/TextField';
import { Theme } from '../../config/theme';
import { request } from '../../utils/Api';
import { ConversationHeader } from './conversation-header';
import { MessageInput } from './message-input';
import { MessageList } from './message-list';

const CLOSE_ICON = require('../../components/MatchRequestModal/close.svg');

const openModalKeyFrames = {
  from: {
    transform: 'scale(0.8)',
    animationTimingFunction: 'ease-in',
  },
  to: {
    transform: 'scale(1)',
    animationTimingFunction: 'ease-in',
  },
};

const styles = StyleSheet.create({
  conversationContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 82px)',
    // backgroundColor: '#f0f2f5',
    [Theme.mobile_query]: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: 'calc(var(--vh, 1vh) * 100)',
      width: '100vw',
    },
  },
  conversationTopBar: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
    width: '100%',
    [Theme.mobile_query]: {
      position: 'fixed',
      top: 80,
      left: 0,
      right: 0,
      zIndex: 99999,
      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
    },
  },
  topBarContent: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '1rem',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    [Theme.mobile_query]: {
      padding: '0.75rem',
      minHeight: '60px', // Ensure minimum height for touch targets
      backgroundColor: '#ffffff', // Ensure background is visible
    },
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.5rem',
    marginRight: '1rem',
    color: '#333333',
    padding: '0.5rem',
    [Theme.mobile_query]: {
      fontSize: '1.25rem',
      marginRight: '0.5rem',
      minWidth: '44px',
      minHeight: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  centeredContainer: {
    maxWidth: '960px',
    margin: '0 auto',
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    [Theme.mobile_query]: {
      maxWidth: '100%',
      marginTop: '60px', // Account for fixed header
      marginBottom: '72px', // Account for fixed input
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: '#6c757d',
    fontSize: '1rem',
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    height: '100%',
    color: '#dc3545',
    fontSize: '1rem',
    padding: '2rem',
    textAlign: 'center',
  },
  retryButton: {
    marginTop: '1rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#0084ff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    ':hover': {
      backgroundColor: '#0056b3',
    },
  },
  mobileInputWrapper: {
    [Theme.mobile_query]: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 10,
    },
  },
  modalContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: 590,
    maxWidth: '100vw',
    backgroundColor: Theme.white,
    borderRadius: 5,
    overflow: 'hidden',
    outline: 'none',
    margin: 'auto auto',
    animationName: openModalKeyFrames,
    animationDuration: '100ms',
    animationDirection: 'alternate',

    [Theme.mobile_query]: {
      width: '100%',
      height: '100%',
      borderRadius: 0,
    },
  },
  modalWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box',
    padding: '0px 60px 40px',

    [Theme.mobile_query]: {
      padding: '0px 20px 20px',
    },
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '50px 0px 35px',

    [Theme.mobile_query]: {
      padding: '40px 0px 25px',
    },
  },
  modalTitle: {
    marginBottom: 0,
    fontFamily: Theme.font_family_heading,
    fontSize: 26,
    fontWeight: '900',
    textAlign: 'center',

    [Theme.mobile_query]: {
      fontSize: 22,
      paddingRight: 30,
      paddingLeft: 30,
    },
  },
  closeIcon: {
    position: 'absolute',
    right: 15,
    top: 15,
    outline: 'none',
    cursor: 'pointer',
    transition: 'opacity 180ms ease-in-out',
    ':hover': {
      opacity: 0.6,
    },
  },
  modalBody: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  modalDescription: {
    marginBottom: 25,
    fontFamily: Theme.font_family_primary,
    fontSize: 16,
    lineHeight: 1.5,
    color: '#4A4A4A',
    textAlign: 'center',
  },
  modalSubtext: {
    marginTop: 15,
    marginBottom: 25,
    fontFamily: Theme.font_family_primary,
    fontSize: 14,
    lineHeight: 1.5,
    color: '#828282',
    textAlign: 'center',
  },
  inputContainer: {
    position: 'relative',
    width: '100%',
    marginBottom: 30,
  },
  textAreaField: {
    display: 'flex',
    flexDirection: 'column',
    height: 150,
    width: '100%',
    marginBottom: 10,

    ':not(.a) > *': {
      flexGrow: 1,
    },
  },
  modalActions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    gap: 15,

    [Theme.mobile_query]: {
      flexDirection: 'column',
      gap: 10,
    },
  },
  cancelButton: {
    width: 150,

    [Theme.mobile_query]: {
      width: '100%',
    },
  },
  submitButton: {
    width: 150,

    [Theme.mobile_query]: {
      width: '100%',
    },
  },
});

class Conversation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      reportModalOpen: false,
      selectedMessage: null,
      reportReason: '',
      reportSubmitting: false,
      unfriendModalOpen: false,
      unfriendSubmitting: false,
      reportConversationModalOpen: false,
      reportConversationReason: '',
      reportConversationSubmitting: false,
    };

    this.messageListRef = React.createRef();
    this.pollingInterval = null;

    this.handleSendMessage = this.handleSendMessage.bind(this);
    this.handleDeleteMessage = this.handleDeleteMessage.bind(this);
    this.handleReportMessage = this.handleReportMessage.bind(this);
    this.handleSubmitReport = this.handleSubmitReport.bind(this);
    this.handleUnfriend = this.handleUnfriend.bind(this);
    this.handleSubmitUnfriend = this.handleSubmitUnfriend.bind(this);
    this.handleVisitProfile = this.handleVisitProfile.bind(this);
    this.handleReportConversation = this.handleReportConversation.bind(this);
    this.handleSubmitReportConversation = this.handleSubmitReportConversation.bind(this);
    this.scrollToBottom = this.scrollToBottom.bind(this);
    this.fetchMessages = this.fetchMessages.bind(this);
    this.formatMessages = this.formatMessages.bind(this);
    this.getConversationInfo = this.getConversationInfo.bind(this);
    this.getUserImage = this.getUserImage.bind(this);
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleListClick = this.handleListClick.bind(this);
    this.handleConversationOpened = this.handleConversationOpened.bind(this);
    this.syncUnreadCount = this.syncUnreadCount.bind(this);

    this.needsUnreadRefresh = false;
  }

  componentDidMount() {
    // Hide main mobile navigation when viewing conversations
    if (Theme.isMobile) {
      const mobileNav = document.querySelector('[class*="mobile_nav"]');
      const dashboardHeader = document.querySelector('[class*="dashboard_header"]');
      const mobileHeader = document.querySelector('[class*="mobile_header"]');

      if (mobileNav) {
        mobileNav.style.setProperty('display', 'none', 'important');
      }
      if (dashboardHeader) {
        dashboardHeader.style.setProperty('display', 'none', 'important');
      }
      if (mobileHeader) {
        mobileHeader.style.setProperty('display', 'none', 'important');
      }
    }

    // Hide doorbell widget when viewing conversations (for all devices)
    const hideDoorbellWidget = () => {
      // Try multiple possible selectors
      const selectors = ['#doorbell-button', '#doorbell', '.doorbell-button', '[id*="doorbell"]'];

      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
        });
      });

      // Also hide the doorbell container div that has inline styles
      const doorbellContainer = document.querySelector('div[style*="doorbell"]');
      if (doorbellContainer) {
        doorbellContainer.style.setProperty('display', 'none', 'important');
      }
    };

    // Try to hide immediately
    hideDoorbellWidget();

    // Also try after a delay in case it loads asynchronously
    setTimeout(hideDoorbellWidget, 500);
    setTimeout(hideDoorbellWidget, 1000);
    setTimeout(hideDoorbellWidget, 2000);

    // Use MutationObserver to catch dynamically added elements
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          hideDoorbellWidget();
        }
      });
    });

    // Start observing the document body for added nodes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Store observer reference for cleanup
    this.doorbellObserver = observer;

    // Mobile-specific setup
    if (Theme.isMobile) {
      // Prevent zooming on mobile by setting viewport meta tag
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        this.originalViewportContent = viewportMeta.getAttribute('content');
        viewportMeta.setAttribute(
          'content',
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
        );
      }

      // Handle viewport height changes (keyboard open/close)
      const handleViewportChange = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };

      // Initial set
      handleViewportChange();

      // Update on resize
      window.addEventListener('resize', handleViewportChange);
      this.handleViewportChange = handleViewportChange;

      // Ensure body doesn't scroll
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }

    // Initial fetch
    this.handleConversationOpened();
    const initialFetch = this.fetchMessages();

    if (!initialFetch) {
      this.syncUnreadCount();
    }

    // Set up polling
    this.pollingInterval = setInterval(() => {
      this.fetchMessages();
    }, 2000);

    // Scroll to bottom on initial load
    setTimeout(() => {
      this.scrollToBottom();
    }, 100);
  }

  componentDidUpdate(prevProps) {
    const { id, messaging } = this.props;
    const conversationMessages = id ? messaging.messages[id] || {} : {};
    const prevMessages = id ? prevProps.messaging.messages[id] || {} : {};

    if (
      (conversationMessages.data && conversationMessages.data.length) !==
      (prevMessages.data && prevMessages.data.length)
    ) {
      this.scrollToBottom();
    }

    if (id && id !== prevProps.id) {
      this.handleConversationOpened();
      const fetchResult = this.fetchMessages();
      if (!fetchResult) {
        this.syncUnreadCount();
      }
    } else if (id) {
      const numericId = parseInt(id, 10);

      if (!Number.isNaN(numericId)) {
        const conversations = messaging.conversations.data || [];
        const prevConversations = prevProps.messaging.conversations.data || [];

        const currentConversation = conversations.find(
          conversation => conversation && conversation.id === numericId,
        );
        const previousConversation = prevConversations.find(
          conversation => conversation && conversation.id === numericId,
        );

        if (
          currentConversation &&
          typeof currentConversation.unreadCount === 'number' &&
          currentConversation.unreadCount > 0 &&
          (!previousConversation ||
            previousConversation.unreadCount !== currentConversation.unreadCount)
        ) {
          this.handleConversationOpened();
          const refreshResult = this.fetchMessages();
          if (!refreshResult) {
            this.syncUnreadCount();
          }
        }
      }
    }
  }

  componentWillUnmount() {
    // Restore main mobile navigation
    if (Theme.isMobile) {
      const mobileNav = document.querySelector('[class*="mobile_nav"]');
      const dashboardHeader = document.querySelector('[class*="dashboard_header"]');
      const mobileHeader = document.querySelector('[class*="mobile_header"]');

      if (mobileNav) {
        mobileNav.style.removeProperty('display');
      }
      if (dashboardHeader) {
        dashboardHeader.style.removeProperty('display');
      }
      if (mobileHeader) {
        mobileHeader.style.removeProperty('display');
      }
    }

    if (this.doorbellObserver) {
      this.doorbellObserver.disconnect();
    }

    // Don't restore doorbell widget immediately to prevent auto-open
    // Instead, add a CSS class that we'll remove after navigation completes
    const style = document.createElement('style');
    style.id = 'doorbell-hide-temp';
    style.innerHTML = `
      #doorbell-button, #doorbell, .doorbell-button, [id*="doorbell"], div[style*="doorbell"] {
        display: none !important;
        visibility: hidden !important;
      }
    `;
    document.head.appendChild(style);

    // Remove the temporary hiding after navigation completes
    setTimeout(() => {
      const tempStyle = document.getElementById('doorbell-hide-temp');
      if (tempStyle) {
        tempStyle.remove();
      }
    }, 500);

    // Restore mobile viewport if changed
    if (Theme.isMobile) {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta && this.originalViewportContent) {
        viewportMeta.setAttribute('content', this.originalViewportContent);
      }

      // Remove viewport handler
      if (this.handleViewportChange) {
        window.removeEventListener('resize', this.handleViewportChange);
      }

      // Restore body position
      document.body.style.position = '';
      document.body.style.width = '';
    }

    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  getUserImage(otherUser) {
    if (!otherUser) return null;

    if (otherUser.image_url && otherUser.image_url.length > 10) {
      return otherUser.image_url;
    }

    if (otherUser.profile_photo && otherUser.profile_photo.length > 10) {
      return otherUser.profile_photo;
    }

    return null;
  }

  getConversationInfo() {
    const { id, messaging, user } = this.props;

    // First try to get info from the conversations list (already loaded in inbox)
    const conversations = messaging.conversations.data || [];
    const currentConversation = conversations.find(conv => conv.id === parseInt(id, 10));

    if (currentConversation && currentConversation.otherUser) {
      return {
        name:
          currentConversation.otherUser.name || currentConversation.otherUser.email || 'Unknown',
        status: 'Online', // Placeholder
        userId: currentConversation.otherUser.id,
        image: this.getUserImage(currentConversation.otherUser),
      };
    }

    // Fallback: try to get info from messages if conversation data not available
    const conversationMessages = messaging.messages[id] || {};
    const messages = conversationMessages.data || [];

    if (messages.length === 0) {
      return {
        name: 'Loading...',
        status: '',
        userId: null,
        image: null,
      };
    }

    // Get the other participant from the first message
    // Note: Messages only have 'sender', not 'receiver'
    const firstMessage = messages[0] || {};
    if (!firstMessage.sender) {
      return {
        name: 'Loading...',
        status: '',
        userId: null,
        image: null,
      };
    }

    // Determine who the other user is based on the sender
    // If the sender is the current user, we need to look at different messages
    const otherUserMessage = messages.find(msg => msg.sender && msg.sender.id !== user.id);
    const otherUser = otherUserMessage ? otherUserMessage.sender : null;

    // If all messages are from current user, use the conversation data from backend
    if (!otherUser && messages.length > 0) {
      // This shouldn't happen in normal flow, but handle it gracefully
      return {
        name: 'User',
        status: '',
        userId: null,
        image: null,
      };
    }

    return {
      name: otherUser ? otherUser.name || otherUser.email || 'Unknown' : 'Unknown',
      status: 'Online', // Placeholder
      userId: otherUser ? otherUser.id : null,
      image: this.getUserImage(otherUser),
    };
  }

  scrollToBottom() {
    if (this.messageListRef.current) {
      this.messageListRef.current.scrollTop = this.messageListRef.current.scrollHeight;
    }
  }

  handleInputFocus() {
    // Scroll to bottom when input is focused
    // Add a small delay to ensure keyboard is shown on mobile
    setTimeout(() => {
      this.scrollToBottom();
    }, 300);
  }

  handleListClick() {
    // On mobile, unfocus the input when clicking the message list
    if (Theme.isMobile) {
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'INPUT') {
        activeElement.blur();
      }
    }
  }

  handleConversationOpened() {
    const { id, dispatch } = this.props;

    if (!id) {
      return null;
    }

    this.needsUnreadRefresh = true;
    return dispatch(markConversationAsRead(id));
  }

  syncUnreadCount(options = {}) {
    const { force = false } = options;
    const { dispatch, user } = this.props;

    if (!force && !this.needsUnreadRefresh) {
      return Promise.resolve();
    }

    if (user && user.access_token) {
      this.needsUnreadRefresh = false;
      const result = dispatch(getUnreadMessageCount(user.access_token));

      if (result && typeof result.catch === 'function') {
        result.catch(() => {});
      }

      return result;
    }

    this.needsUnreadRefresh = false;
    return Promise.resolve();
  }

  fetchMessages() {
    const { id, dispatch, user } = this.props;

    if (id && user && user.access_token) {
      const result = dispatch(getMessages(id, 1, 50, user.access_token));

      if (result && typeof result.then === 'function') {
        result
          .then(response => {
            if (this.needsUnreadRefresh) {
              return this.syncUnreadCount();
            }

            return response;
          })
          .catch(() => {});
      }

      return result;
    }

    return null;
  }

  handleSendMessage(content, attachmentIds = []) {
    const { id, dispatch, user } = this.props;
    if ((content.trim() || attachmentIds.length > 0) && user && user.access_token) {
      dispatch(sendMessage(id, content, user.access_token, user.id, attachmentIds));
      // Immediately scroll to bottom to show the optimistically added message
      // Using setTimeout to ensure DOM has updated with the new message
      setTimeout(() => {
        this.scrollToBottom();
      }, 0);
    }
  }

  handleDeleteMessage(messageId) {
    const { dispatch, user } = this.props;
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    if (messageId && user && user.access_token) {
      dispatch(deleteMessage(messageId, user.access_token));
    }
  }

  handleReportMessage(message) {
    this.setState({
      selectedMessage: message,
      reportReason: '',
      reportModalOpen: true,
    });
  }

  handleSubmitReport() {
    const { selectedMessage, reportReason } = this.state;
    const { dispatch, user } = this.props;

    if (!selectedMessage || !reportReason.trim() || !user || !user.access_token) {
      return;
    }

    // Close modal optimistically
    this.setState({
      reportModalOpen: false,
      reportSubmitting: false,
      reportReason: '',
      selectedMessage: null,
    });

    dispatch(reportMessage(selectedMessage.id, reportReason, user.access_token))
      .then(() => {
        toast.success('Report submitted successfully.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
        });
      })
      .catch(() => {
        toast.error('Failed to submit report. Please try again.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
        });
      });
  }

  handleUnfriend() {
    this.setState({
      unfriendModalOpen: true,
    });
  }

  handleSubmitUnfriend() {
    const { id, dispatch, user, onBackClick } = this.props;

    if (!user || !user.access_token) {
      return;
    }

    // Close modal optimistically
    this.setState({
      unfriendModalOpen: false,
      unfriendSubmitting: false,
    });

    dispatch(endConversation(id, user.access_token))
      .then(() => {
        toast.info('Conversation ended successfully.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
        });
        // Redirect to inbox after successful unfriend
        if (onBackClick) {
          setTimeout(() => {
            onBackClick();
          }, 500);
        }
      })
      .catch(() => {
        toast.error('Failed to end conversation. Please try again.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
        });
      });
  }

  handleVisitProfile() {
    const conversationInfo = this.getConversationInfo();
    if (conversationInfo.userId) {
      this.props.history.push(`/profile/${conversationInfo.userId}`);
    }
  }

  handleReportConversation() {
    this.setState({
      reportConversationModalOpen: true,
      reportConversationReason: '',
    });
  }

  handleSubmitReportConversation() {
    const { reportConversationReason } = this.state;
    const { dispatch, user } = this.props;
    const conversationInfo = this.getConversationInfo();

    if (
      !conversationInfo.userId ||
      !reportConversationReason.trim() ||
      !user ||
      !user.access_token
    ) {
      return;
    }

    // Close modal optimistically
    this.setState({
      reportConversationModalOpen: false,
      reportConversationSubmitting: false,
      reportConversationReason: '',
    });

    // Report the member (since there's no specific conversation report endpoint)
    dispatch({
      type: 'REPORT_MEMBER_REQUEST',
      payload: new Promise((resolve, reject) => {
        request(
          `member/${conversationInfo.userId}/report`,
          'POST',
          {
            message: reportConversationReason,
            reporter_id: user.id,
            reporter_name: `${user.first_name} ${user.surname}`,
            reported_id: conversationInfo.userId,
            email: user.email,
          },
          { Authorization: `Bearer ${user.access_token}` },
        ).then(
          response => {
            toast.success('Report submitted successfully.', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: false,
              draggable: false,
            });
            resolve(response);
          },
          error => {
            toast.error('Failed to submit report. Please try again.', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: false,
              draggable: false,
            });
            reject(error);
          },
        );
      }),
    });
  }

  formatMessages(apiMessages) {
    const { user } = this.props;
    if (!apiMessages || !user) {
      return [];
    }

    // Map messages to the correct format
    const formattedMessages = apiMessages.map(msg => ({
      id: msg.id,
      content: msg.content,
      sender: msg.sender.id === user.id ? 'user' : 'other',
      originalSender: msg.sender,
      timestamp: new Date(msg.created),
      isRead: msg.isRead,
      isPending: msg.isPending || false, // Preserve the pending flag for optimistic messages
      attachments: msg.attachments || [], // Include attachments
    }));

    // Sort messages in ascending order by timestamp (oldest first)
    return formattedMessages.sort((a, b) => a.timestamp - b.timestamp);
  }

  render() {
    const { id, messaging, onBackClick } = this.props;
    const {
      reportModalOpen,
      reportReason,
      reportSubmitting,
      unfriendModalOpen,
      unfriendSubmitting,
      reportConversationModalOpen,
      reportConversationReason,
      reportConversationSubmitting,
    } = this.state;

    const conversationMessages = messaging.messages[id] || {};
    const loading = conversationMessages.loading || false;
    const error = conversationMessages.error || null;
    const messages = this.formatMessages(conversationMessages.data || []);

    const conversationInfo = this.getConversationInfo();

    return (
      <div className={css(styles.conversationContainer)}>
        <div className={css(styles.conversationTopBar)}>
          <div className={css(styles.topBarContent)}>
            <button onClick={onBackClick} className={css(styles.backButton)}>
              {'\u2190'}
            </button>
            <div style={{ flex: 1 }}>
              <ConversationHeader
                name={conversationInfo.name}
                status={conversationInfo.status}
                image={conversationInfo.image}
                onVisitProfile={this.handleVisitProfile}
                onReportConversation={this.handleReportConversation}
                onUnfriend={this.handleUnfriend}
              />
            </div>
          </div>
        </div>
        <div className={css(styles.centeredContainer)}>
          {loading && messages.length === 0 ? (
            <div className={css(styles.loadingContainer)}>Loading messages...</div>
          ) : error ? (
            <div className={css(styles.errorContainer)}>
              <p>{error}</p>
              <button onClick={this.fetchMessages} className={css(styles.retryButton)}>
                Retry
              </button>
            </div>
          ) : (
            <MessageList
              messages={messages}
              ref={this.messageListRef}
              onDeleteMessage={this.handleDeleteMessage}
              onReportMessage={this.handleReportMessage}
              onListClick={this.handleListClick}
            />
          )}
        </div>
        <div className={css(styles.mobileInputWrapper)}>
          <MessageInput
            onSendMessage={this.handleSendMessage}
            onInputFocus={this.handleInputFocus}
            accessToken={this.props.user ? this.props.user.access_token : null}
          />
        </div>

        {/* Report Message Modal */}
        <Modal
          isOpen={reportModalOpen}
          className={css(styles.modalContainer)}
          style={{
            overlay: {
              backgroundColor: 'rgba(0,0,0,0.67)',
              zIndex: 10,
              display: 'flex',
            },
          }}
          contentLabel="Report Message"
        >
          <div className={css(styles.modalWrapper)}>
            <div className={css(styles.modalHeader)}>
              <h2 className={css(styles.modalTitle)}>Report Message</h2>
              <div
                className={css(styles.closeIcon)}
                onClick={() => this.setState({ reportModalOpen: false, reportReason: '' })}
                role="button"
                tabIndex={0}
              >
                <img src={CLOSE_ICON} alt="Close modal" />
              </div>
            </div>
            <div className={css(styles.modalBody)}>
              <p className={css(styles.modalDescription)}>
                Please provide a reason for reporting this message:
              </p>
              <div className={css(styles.inputContainer)}>
                <TextField
                  textArea
                  name="reportReason"
                  styles={styles.textAreaField}
                  handler={(key, value) => this.setState({ reportReason: value })}
                  value={reportReason}
                  placeholder="Enter your reason here..."
                />
              </div>
              <div className={css(styles.modalActions)}>
                <Button
                  title="Cancel"
                  theme="white"
                  handler={() => this.setState({ reportModalOpen: false, reportReason: '' })}
                  styles={styles.cancelButton}
                />
                <Button
                  title="Submit Report"
                  theme="blue"
                  handler={this.handleSubmitReport}
                  disabled={!reportReason.trim() || reportSubmitting}
                  styles={styles.submitButton}
                />
              </div>
            </div>
          </div>
        </Modal>

        {/* Report Conversation Modal */}
        <Modal
          isOpen={reportConversationModalOpen}
          className={css(styles.modalContainer)}
          style={{
            overlay: {
              backgroundColor: 'rgba(0,0,0,0.67)',
              zIndex: 10,
              display: 'flex',
            },
          }}
          contentLabel="Report Conversation"
        >
          <div className={css(styles.modalWrapper)}>
            <div className={css(styles.modalHeader)}>
              <h2 className={css(styles.modalTitle)}>Report Conversation</h2>
              <div
                className={css(styles.closeIcon)}
                onClick={() =>
                  this.setState({
                    reportConversationModalOpen: false,
                    reportConversationReason: '',
                  })
                }
                role="button"
                tabIndex={0}
              >
                <img src={CLOSE_ICON} alt="Close modal" />
              </div>
            </div>
            <div className={css(styles.modalBody)}>
              <p className={css(styles.modalDescription)}>
                Please provide a reason for reporting this conversation with {conversationInfo.name}
                :
              </p>
              <div className={css(styles.inputContainer)}>
                <TextField
                  textArea
                  name="reportConversationReason"
                  styles={styles.textAreaField}
                  handler={(key, value) => this.setState({ reportConversationReason: value })}
                  value={reportConversationReason}
                  placeholder="Tell us why you'd like to report this conversation..."
                />
              </div>
              <div className={css(styles.modalActions)}>
                <Button
                  title="Cancel"
                  theme="white"
                  handler={() =>
                    this.setState({
                      reportConversationModalOpen: false,
                      reportConversationReason: '',
                    })
                  }
                  styles={styles.cancelButton}
                />
                <Button
                  title="Submit Report"
                  theme="blue"
                  handler={this.handleSubmitReportConversation}
                  disabled={!reportConversationReason.trim() || reportConversationSubmitting}
                  styles={styles.submitButton}
                />
              </div>
            </div>
          </div>
        </Modal>

        {/* Unfriend Modal */}
        <Modal
          isOpen={unfriendModalOpen}
          className={css(styles.modalContainer)}
          style={{
            overlay: {
              backgroundColor: 'rgba(0,0,0,0.67)',
              zIndex: 10,
              display: 'flex',
            },
          }}
          contentLabel="End Conversation"
        >
          <div className={css(styles.modalWrapper)}>
            <div className={css(styles.modalHeader)}>
              <h2 className={css(styles.modalTitle)}>End Conversation</h2>
              <div
                className={css(styles.closeIcon)}
                onClick={() => this.setState({ unfriendModalOpen: false })}
                role="button"
                tabIndex={0}
              >
                <img src={CLOSE_ICON} alt="Close modal" />
              </div>
            </div>
            <div className={css(styles.modalBody)}>
              <p className={css(styles.modalDescription)}>
                Are you sure you want to unfriend {conversationInfo.name} and end this conversation?
              </p>
              <p className={css(styles.modalSubtext)}>
                This action will end the match and you won&apos;t be able to message each other
                anymore.
              </p>
              <div className={css(styles.modalActions)}>
                <Button
                  title="Cancel"
                  theme="white"
                  handler={() => this.setState({ unfriendModalOpen: false })}
                  disabled={unfriendSubmitting}
                  styles={styles.cancelButton}
                />
                <Button
                  title="End Conversation"
                  theme="blue"
                  handler={this.handleSubmitUnfriend}
                  disabled={unfriendSubmitting}
                  styles={styles.submitButton}
                />
              </div>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

Conversation.propTypes = {
  id: PropTypes.string.isRequired,
  onBackClick: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  user: PropTypes.object,
  messaging: PropTypes.object,
  history: PropTypes.object.isRequired,
};

const mapMessagingStateToProps = state => ({
  messaging: state.messaging,
  user: state.user,
});

export default connect(mapMessagingStateToProps)(Conversation);



// WEBPACK FOOTER //
// ./src/views/MessengerConversation/conversation.js